import {forwardRef, useImperativeHandle, useState} from "react";
import type {IMassInputV5Props} from "../../../../modules/abstract/sidepanel/tabs/details/field/mass/IMassInput";
import {ObjectUtils} from "../../../../../utils/data/ObjectUtils";
import {FieldInputsV5} from "../../../../widgets/input/clicktoedit/InputUtils";
import {SelectInputV5} from "../../../input/select/SelectInputV5";
import {SingleLineInputV5} from "../SingleLineInputV5";
import {ConfirmWindowV5} from "../../../popup/ConfirmWindowV5";
import {useAppStore} from "../../../../../StateManager";
import type {IMultiSelectOption} from "../../../../../data/models/field/datatypes/MultiSelect";
import type {IMultiSelectFieldSettingsDefinition} from "../../../../modules/settings/modules/field/datatypes/MultiSelectFieldSettings";
import {FieldDataType, XyiconFeature} from "../../../../../generated/api/base";
import type {User} from "../../../../../data/models/User";
import type {IModel} from "../../../../../data/models/Model";
import {OptionSelectorInputStyled} from "./MassFieldInputV5";
import {MassFieldInputStyled} from "./MassFieldInputStyled";

type ISelectOptionId = "add" | "remove" | "overwrite";

interface ISelectOption {
	id: ISelectOptionId;
	label: string;
}

const _options: ISelectOption[] = [
	{
		id: "add",
		label: "Add to List",
	},
	{
		id: "remove",
		label: "Remove from List",
	},
	{
		id: "overwrite",
		label: "Overwrite List",
	},
];

export const MassFieldMultiInputV5 = forwardRef((props: IMassInputV5Props, ref) => {
	const appState = useAppStore((store) => store.appState);

	const [selectedOption, setSelectedOption] = useState<ISelectOptionId>("add");
	const [inputValue, setInputValue] = useState<IMultiSelectOption[] | string[]>([]);

	const isPeopleMultiple = props.field.dataType === FieldDataType.PeopleMultipleChoiceList;

	const getInputIds = () => {
		if (isPeopleMultiple) {
			return Array.isArray(inputValue) ? (inputValue as string[]) : [];
		}

		return (inputValue as IMultiSelectOption[]).map((value) => value.value);
	};

	const getSelectedUsers = (userIds: string[]) =>
		userIds.map((userId) => appState.actions.getFeatureItemById<User>(userId, XyiconFeature.User)).filter((user): user is User => !!user);

	const getCurrentIds = (item: IModel) => {
		const currentValue: string[] | User[] = appState.actions.getOwnFieldValue(item, props.field.refId) ?? [];

		if (isPeopleMultiple) {
			return (currentValue as User[]).map((user) => user.id);
		}

		return currentValue as string[];
	};

	const onOptionChange = (option: ISelectOption) => {
		setSelectedOption(option.id);
	};

	const onInputChange = (value: IMultiSelectOption[] | string[]) => {
		setInputValue(value);
	};

	const getEditInput = () => {
		const {field} = props;

		let InputComponent = FieldInputsV5[field.dataType];

		InputComponent = InputComponent || SingleLineInputV5;

		const inputIds = getInputIds();
		const inputValueForComponent = isPeopleMultiple ? getSelectedUsers(inputIds) : inputIds;

		return (
			<InputComponent
				value={inputValueForComponent}
				dataTypeSettings={field.dataTypeSettings}
				onChange={onInputChange}
				onInput={onInputChange}
				onFocusLossForceBlur={true}
			/>
		);
	};

	useImperativeHandle(ref, () => ({
		async onApply() {
			const {field, items} = props;

			const inputIds = getInputIds();
			const itemsToUpdate = items.filter((item) => {
				const values = getCurrentIds(item);

				if (selectedOption === "add") {
					return inputIds.some((value) => !values.includes(value));
				} else if (selectedOption === "remove") {
					return inputIds.some((value) => values.includes(value));
				} else if (selectedOption === "overwrite") {
					return !ObjectUtils.compare(inputIds, values);
				}
			});
			const confirmed = await ConfirmWindowV5.open(`Are you sure you want to update ${itemsToUpdate.length} items?`, "Confirm Update", {
				ok: "Yes",
				cancel: "Cancel",
			});

			if (confirmed) {
				if (selectedOption === "add") {
					for (const item of itemsToUpdate) {
						if (isPeopleMultiple) {
							const valuesAsIds = getCurrentIds(item);
							const values = valuesAsIds.slice();

							for (const id of inputIds) {
								if (!valuesAsIds.includes(id)) {
									values.push(id);
								}
							}

							appState.actions.updateFields([item], {
								[field.refId]: values,
							});
						} else {
							const valuesAsString = (appState.actions.getOwnFieldValue(item, field.refId) || []).slice() as string[];
							const values = (field.dataTypeSettings as IMultiSelectFieldSettingsDefinition).choiceList.filter((c: IMultiSelectOption) =>
								valuesAsString.includes(c.value),
							);

							for (const v of inputValue as IMultiSelectOption[]) {
								if (!valuesAsString.includes(v.value)) {
									values.push(v);
								}
							}

							appState.actions.updateFields([item], {
								[field.refId]: values,
							});
						}
					}
				} else if (selectedOption === "remove") {
					for (const item of itemsToUpdate) {
						if (isPeopleMultiple) {
							const valuesAsIds = getCurrentIds(item);
							let values = valuesAsIds.slice();

							for (const id of inputIds) {
								if (valuesAsIds.includes(id)) {
									values = values.filter((value) => value !== id);
								}
							}

							appState.actions.updateFields([item], {
								[field.refId]: values,
							});
						} else {
							const valuesAsString = (appState.actions.getOwnFieldValue(item, field.refId) || []).slice() as string[];
							let values = (field.dataTypeSettings as IMultiSelectFieldSettingsDefinition).choiceList.filter((c: IMultiSelectOption) =>
								valuesAsString.includes(c.value),
							);

							for (const v of inputValue as IMultiSelectOption[]) {
								if (valuesAsString.includes(v.value)) {
									values = values.filter((vv) => vv.value !== v.value);
								}
							}

							appState.actions.updateFields([item], {
								[field.refId]: values,
							});
						}
					}
				} else if (selectedOption === "overwrite") {
					appState.actions.updateMassFields(itemsToUpdate, {
						[field.refId]: inputValue,
					});
				}

				props.onClose();
			}
		},
	}));

	return (
		<MassFieldInputStyled className="MassFieldInput">
			<div className="container">
				<OptionSelectorInputStyled>
					<SelectInputV5
						options={_options}
						renderOptionWhenClosed={(option) => option.label}
						selected={_options.find((option) => option.id === selectedOption)}
						onChange={onOptionChange}
					/>
				</OptionSelectorInputStyled>
				{getEditInput()}
			</div>
		</MassFieldInputStyled>
	);
});
